'use client';

import { useMemo, useRef, type ReactElement } from 'react';
import { DraggableContainer } from '@/app/elements/draggable-wrapper/draggable-container';
import { classes } from '@/utils/classes/classes';
import { useStore } from '@/store/store';
import type { RootState } from '@/store/store';
import { sortUidsByLaunchOrder } from '@/utils/dynamic-ordering';
import { useGameZoneImpression } from '@/hooks/use-game-zone-impression/use-game-zone-impression';
import type { GameTrackingInfo } from '@/types/games';

const EMPTY_ARRAY: string[] = [];

type Props = {
    gameElements: ReactElement[];
    gameUids: string[];
    zoneUid?: string;
    zoneDynamicOrderingFromCms?: boolean;
    shouldApplyDynamicSorting: boolean;
    isMobile: boolean;
    isAnimated: boolean;
    gridClassName: string;
    games?: GameTrackingInfo[];
    zoneName?: string;
    zoneType?: string;
    zoneLayout?: string;
    bannerId?: string;
};

export const CollectionZoneClient = ({
    gameElements,
    gameUids,
    zoneUid,
    zoneDynamicOrderingFromCms = false,
    shouldApplyDynamicSorting,
    isMobile,
    isAnimated,
    gridClassName,
    games = [],
    zoneName,
    zoneType = 'collection',
    zoneLayout,
    bannerId = 'collection'
}: Props) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const launchedGames = useStore((state: RootState) =>
        zoneUid ? (state.dynamicOrdering.launchedGamesByZone[zoneUid] ?? EMPTY_ARRAY) : EMPTY_ARRAY
    );

    const gamesInDomOrder = useMemo(() => {
        if (!games.length || !shouldApplyDynamicSorting || !launchedGames.length) {
            return games;
        }
        const sortedUids = sortUidsByLaunchOrder(gameUids, launchedGames);
        const gameByUid = new Map(games.map((g) => [g.uid, g]));
        return sortedUids
            .map((uid) => gameByUid.get(uid))
            .filter((g): g is GameTrackingInfo => g !== undefined && g !== null);
    }, [games, gameUids, launchedGames, shouldApplyDynamicSorting]);

    useGameZoneImpression({
        containerRef,
        items: gamesInDomOrder,
        zoneConfig: {
            zoneName: zoneName ?? 'Collection',
            zoneType: zoneType ?? 'collection',
            zoneLayout: zoneLayout ?? null
        }
    });

    const elementByUid = useMemo(
        () => new Map(gameElements.map((element, index) => [gameUids[index], element])),
        [gameElements, gameUids]
    );

    const sortedElements = useMemo(() => {
        if (!shouldApplyDynamicSorting || !launchedGames.length) {
            return gameElements;
        }

        const sortedUids = sortUidsByLaunchOrder(gameUids, launchedGames);
        return sortedUids.map((uid) => elementByUid.get(uid));
    }, [gameElements, elementByUid, gameUids, launchedGames, shouldApplyDynamicSorting]);

    return (
        <div
            ref={containerRef}
            data-t-bannerid={bannerId}
            data-t-zonetype={zoneType}
            data-t-dynamicordering={zoneDynamicOrderingFromCms ? 'true' : 'false'}>
            <DraggableContainer
                indicators={!isMobile}
                pagination={isAnimated}
                // use-pagination.hook.ts uses querySelectorAll which expects css selector -- DO NOT MODIFY THIS DOT --
                className={classes(isAnimated && '.animated-trailer')}>
                <div className={gridClassName}>{sortedElements}</div>
            </DraggableContainer>
        </div>
    );
};
